/**
 * @fileoverview Navigation Context for tracking user navigation history and recent items.
 *
 * This context provides comprehensive navigation tracking functionality including:
 * - Navigation history tracking with localStorage persistence
 * - Recent items management for quick access
 * - Page title and type classification
 * - Entity identification and categorization
 * - History management and cleanup
 * - Breadcrumb and navigation data
 *
 * Used throughout the application for:
 * - Navigation breadcrumbs and history
 * - Recent items in sidebar or quick access
 * - User journey tracking and analytics
 * - Back/forward navigation patterns
 * - Quick access to recently viewed entities
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * React Context for navigation tracking and history management
 * @type {React.Context<NavigationContextValue>}
 */
const NavigationContext = createContext();

/**
 * Custom hook to access the navigation context.
 * Must be used within a NavigationProvider component.
 *
 * @function useNavigation
 * @returns {NavigationContextValue} The navigation context value containing history and methods
 * @throws {Error} When used outside of NavigationProvider
 *
 * @example
 * ```jsx
 * function BreadcrumbComponent() {
 *   const { navigationHistory, getNavigationHistory } = useNavigation();
 *   const recentPages = getNavigationHistory(5);
 *
 *   return (
 *     <nav>
 *       {recentPages.map(page => (
 *         <Link key={page.id} to={page.path}>{page.title}</Link>
 *       ))}
 *     </nav>
 *   );
 * }
 * ```
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

/**
 * Navigation Provider component that tracks user navigation and manages history.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to navigation context
 * @returns {JSX.Element} Provider component wrapping children with navigation context
 *
 * @example
 * ```jsx
 * function App() {
 *   return (
 *     <Router>
 *       <NavigationProvider>
 *         <Routes>
 *           <Route path="/dashboard" element={<Dashboard />} />
 *           <Route path="/clients/:id" element={<ClientDetail />} />
 *         </Routes>
 *       </NavigationProvider>
 *     </Router>
 *   );
 * }
 * ```
 */
export const NavigationProvider = ({ children }) => {
  const location = useLocation();
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [recentItems, setRecentItems] = useState([]);

  // Load navigation history from localStorage on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('navigationHistory');
    const savedRecentItems = localStorage.getItem('recentItems');
    
    if (savedHistory) {
      try {
        setNavigationHistory(JSON.parse(savedHistory));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to parse navigation history:', error);
        }
      }
    }

    if (savedRecentItems) {
      try {
        setRecentItems(JSON.parse(savedRecentItems));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to parse recent items:', error);
        }
      }
    }
  }, []);

  // Track navigation changes
  useEffect(() => {
    const currentPath = location.pathname;
    const currentSearch = location.search;
    const fullPath = currentPath + currentSearch;

    // Skip certain paths
    const skipPaths = ['/login', '/reset_password', '/register', ''];
    if (skipPaths.some(path => currentPath.startsWith(path)) || currentPath === '/') {
      return;
    }

    // Create navigation item
    const navigationItem = {
      path: fullPath,
      pathname: currentPath,
      search: currentSearch,
      timestamp: Date.now(),
      title: getPageTitle(currentPath),
      type: getPageType(currentPath),
      id: generateNavigationId(currentPath)
    };

    setNavigationHistory(prevHistory => {
      // Don't add if it's the same as the last item
      if (prevHistory.length > 0 && prevHistory[0].path === fullPath) {
        return prevHistory;
      }

      // Add to beginning and limit to 50 items
      const newHistory = [navigationItem, ...prevHistory.slice(0, 49)];
      
      // Save to localStorage
      localStorage.setItem('navigationHistory', JSON.stringify(newHistory));
      
      return newHistory;
    });

    // Update recent items if this is a detail page
    if (isDetailPage(currentPath)) {
      const recentItem = {
        ...navigationItem,
        entityId: getEntityId(currentPath),
        entityType: getEntityType(currentPath)
      };

      setRecentItems(prevItems => {
        // Remove if already exists
        const filteredItems = prevItems.filter(item => 
          !(item.entityType === recentItem.entityType && item.entityId === recentItem.entityId)
        );
        
        // Add to beginning and limit to 20 items
        const newItems = [recentItem, ...filteredItems.slice(0, 19)];
        
        // Save to localStorage
        localStorage.setItem('recentItems', JSON.stringify(newItems));
        
        return newItems;
      });
    }
  }, [location.pathname, location.search]);

  /**
   * Helper function to generate page titles from pathname
   * @param {string} pathname - The current pathname
   * @returns {string} Human-readable page title
   */
  const getPageTitle = (pathname) => {
    const segments = pathname.split('/').filter(x => x);

    const titleMap = {
      'dashboard': 'Dashboard',
      'clients': 'Clients',
      'time-entries': 'Time Entries',
      'invoices': 'Invoices',
      'reports': 'Reports',
      'settings': 'Settings',
      'profile': 'Profile',
      'payments': 'Payments',
      'activities': 'Activities',
      'users': 'Users',
      'admin': 'Admin',
      'backup': 'Backup'
    };

    if (segments.length === 0 || segments[0] === 'dashboard') {
      return 'Dashboard';
    }

    const mainSection = segments[0];
    let title = titleMap[mainSection] || mainSection.charAt(0).toUpperCase() + mainSection.slice(1);

    // Add context for detail pages
    if (segments.length > 1) {
      const action = segments[segments.length - 1];
      if (action === 'new') title += ' - New';
      else if (action === 'edit') title += ' - Edit';
      else if (action === 'view') title += ' - View';
      else if (/^\d+$/.test(action)) title += ` - #${action}`;
    }

    return title;
  };

  /**
   * Determines the page type based on pathname segments
   * @param {string} pathname - The current pathname
   * @returns {'dashboard'|'create'|'edit'|'detail'|'list'} The page type
   */
  const getPageType = (pathname) => {
    const segments = pathname.split('/').filter(x => x);

    if (segments.length === 0 || segments[0] === 'dashboard') return 'dashboard';
    if (segments.includes('new')) return 'create';
    if (segments.includes('edit')) return 'edit';
    if (segments.length > 1 && /^\d+/.test(segments[segments.length - 1])) return 'detail';

    return 'list';
  };

  /**
   * Checks if the current page is a detail page (viewing a specific entity)
   * @param {string} pathname - The current pathname
   * @returns {boolean} True if this is a detail page
   */
  const isDetailPage = (pathname) => {
    const segments = pathname.split('/').filter(x => x);
    return segments.length > 1 && (/^\d+$/.test(segments[segments.length - 1]) ||
           /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segments[segments.length - 1]));
  };

  /**
   * Extracts entity ID from pathname (numeric ID or UUID)
   * @param {string} pathname - The current pathname
   * @returns {string|null} The entity ID or null if not found
   */
  const getEntityId = (pathname) => {
    const segments = pathname.split('/').filter(x => x);
    const lastSegment = segments[segments.length - 1];

    if (/^\d+$/.test(lastSegment) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(lastSegment)) {
      return lastSegment;
    }

    return null;
  };

  /**
   * Determines the entity type from pathname
   * @param {string} pathname - The current pathname
   * @returns {string} The entity type (client, invoice, time-entry, user, etc.)
   */
  const getEntityType = (pathname) => {
    const segments = pathname.split('/').filter(x => x);

    if (segments.includes('clients')) return 'client';
    if (segments.includes('invoices')) return 'invoice';
    if (segments.includes('time-entries')) return 'time-entry';
    if (segments.includes('users')) return 'user';

    return segments[0] || 'unknown';
  };

  /**
   * Generates a unique navigation ID
   * @param {string} pathname - The current pathname
   * @returns {string} Unique navigation ID
   */
  const generateNavigationId = (pathname) => {
    return `nav-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  /** Clears all navigation history from state and localStorage */
  const clearHistory = () => {
    setNavigationHistory([]);
    localStorage.removeItem('navigationHistory');
  };

  /** Clears all recent items from state and localStorage */
  const clearRecentItems = () => {
    setRecentItems([]);
    localStorage.removeItem('recentItems');
  };

  /**
   * Removes a specific item from navigation history
   * @param {string} id - The navigation item ID to remove
   */
  const removeFromHistory = (id) => {
    setNavigationHistory(prev => {
      const filtered = prev.filter(item => item.id !== id);
      localStorage.setItem('navigationHistory', JSON.stringify(filtered));
      return filtered;
    });
  };

  /**
   * Removes a specific item from recent items
   * @param {string} entityType - The entity type (client, invoice, etc.)
   * @param {string} entityId - The entity ID
   */
  const removeFromRecentItems = (entityType, entityId) => {
    setRecentItems(prev => {
      const filtered = prev.filter(item =>
        !(item.entityType === entityType && item.entityId === entityId)
      );
      localStorage.setItem('recentItems', JSON.stringify(filtered));
      return filtered;
    });
  };

  /**
   * Gets recent items filtered by entity type
   * @param {string} type - The entity type to filter by
   * @returns {Array} Array of recent items of the specified type
   */
  const getRecentItemsByType = (type) => {
    return recentItems.filter(item => item.entityType === type);
  };

  /**
   * Gets navigation history with optional limit
   * @param {number} [limit=10] - Maximum number of items to return
   * @returns {Array} Array of navigation history items
   */
  const getNavigationHistory = (limit = 10) => {
    return navigationHistory.slice(0, limit);
  };

  /**
   * The context value object containing all navigation state and methods
   * @typedef {Object} NavigationContextValue
   * @property {Array} navigationHistory - Full navigation history array
   * @property {Array} recentItems - Recent items for quick access
   * @property {Function} clearHistory - Clear all navigation history
   * @property {Function} clearRecentItems - Clear all recent items
   * @property {Function} removeFromHistory - Remove specific item from history
   * @property {Function} removeFromRecentItems - Remove specific item from recent items
   * @property {Function} getRecentItemsByType - Get recent items filtered by type
   * @property {Function} getNavigationHistory - Get navigation history with limit
   */
  const value = {
    navigationHistory,
    recentItems,
    clearHistory,
    clearRecentItems,
    removeFromHistory,
    removeFromRecentItems,
    getRecentItemsByType,
    getNavigationHistory
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};